<template>
  <div class="panel-card import-panel">
    <div class="section-title-row">
      <div>
        <div class="section-title" style="margin-bottom: 4px">Importo nga CSV</div>
        <p class="panel-subtitle">
          Shablloni ka <strong>Kod produkti</strong>, <strong>Cmim baze</strong> dhe <strong>Cmim</strong>.
          Përdoret vetëm kolona e llojit të zgjedhur.
        </p>
      </div>
    </div>

    <div class="import-layout">
      <div class="import-step">
        <div class="import-step-label">1. Lloji i ndryshimit</div>
        <div class="import-type-switch">
          <button
            type="button"
            class="import-type-btn"
            :class="{ active: po.priceImportTargetField === 'price' }"
            :disabled="po.priceImportApplying"
            @click="po.onPriceImportTargetFieldChange('price')"
          >
            Cmimi
          </button>
          <button
            type="button"
            class="import-type-btn"
            :class="{ active: po.priceImportTargetField === 'default_price' }"
            :disabled="po.priceImportApplying"
            @click="po.onPriceImportTargetFieldChange('default_price')"
          >
            Cmimi baze
          </button>
        </div>
        <p class="import-type-hint">
          {{
            po.priceImportTargetField === "default_price"
              ? "Do të përditësohet kolona Cmim baze (default_price)."
              : "Do të përditësohet kolona Cmim (price)."
          }}
        </p>
      </div>

      <div class="import-step">
        <div class="import-step-label">2. Skedari</div>
        <div class="import-file-row">
          <button type="button" class="button is-small is-light" :disabled="po.priceImportApplying" @click="po.downloadPriceImportTemplate">
            Shkarko shabllonin
          </button>
          <label class="import-file-picker" :class="{ disabled: po.priceImportApplying }">
            <span class="import-file-picker-btn">Zgjidh skedar</span>
            <span class="import-file-name">{{ po.priceImportFile?.name || "Asnje skedar i zgjedhur" }}</span>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.txt"
              :disabled="po.priceImportApplying"
              @click="$event.target.value = null"
              @change="po.onPriceImportFilePicked"
            />
          </label>
        </div>
      </div>

      <div class="import-step import-step-actions">
        <div class="import-step-label">3. Apliko</div>
        <div class="import-actions-row">
          <button
            type="button"
            class="button is-success"
            :class="{ 'is-loading': po.priceImportApplying }"
            :disabled="!po.priceImportValidRows.length || po.priceImportLoading || po.priceImportApplying"
            @click="po.requestApplyPriceImport"
          >
            Vazhdo{{ po.priceImportValidRows.length ? ` (${po.priceImportValidRows.length})` : "" }}
          </button>
          <button
            type="button"
            class="button is-light"
            :disabled="po.priceImportApplying"
            @click="po.clearPriceImportState"
          >
            Pastro
          </button>
          <button
            v-if="po.priceImportErrorDownloadAvailable"
            type="button"
            class="button is-light is-danger"
            :disabled="po.priceImportApplying"
            @click="po.downloadPriceImportErrorsJson"
          >
            Shkarko gabimet (JSON)
          </button>
          <div v-if="po.priceImportPreview" class="import-stats">
            <span class="chip muted">OK: {{ po.priceImportPreview.valid_count || 0 }}</span>
            <span class="chip" :class="(po.priceImportPreview.error_count || 0) > 0 ? '' : 'muted'">
              Gabime: {{ po.priceImportPreview.error_count || 0 }}
            </span>
            <span v-if="po.priceImportOverScheduleCap" class="chip muted">
              Planifikimi ndahet në {{ po.priceImportScheduleSplits }} batch-e (max {{ po.priceScheduleItemCap }})
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="po.priceImportLoading" class="empty-note">Duke lexuar skedarin...</div>
    <div v-if="po.priceImportApplying" class="import-progress">
      <div class="import-progress-label">
        Duke aplikuar {{ po.priceImportProgressLabel }}: {{ po.priceImportProgress.done }} / {{ po.priceImportProgress.total }}
        ({{ po.priceImportProgressPercent }}%)
      </div>
      <div class="import-progress-track">
        <div class="import-progress-bar" :style="{ width: po.priceImportProgressPercent + '%' }"></div>
      </div>
    </div>

    <div class="manual-table-wrap" v-if="po.priceImportPreview?.rows?.length">
      <table class="table is-fullwidth is-narrow audit-table">
        <thead>
          <tr>
            <th>Rreshti</th>
            <th>Kodi</th>
            <th>Produkti</th>
            <th class="has-text-right">
              {{ po.priceImportTargetField === "default_price" ? "Cmim baze aktual" : "Cmim aktual" }}
            </th>
            <th class="has-text-right">
              {{ po.priceImportTargetField === "default_price" ? "Cmim baze i ri" : "Cmim i ri" }}
            </th>
            <th>Statusi</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in po.priceImportPreview.rows"
            :key="`pi-${row.line}-${row.code}`"
            :class="{ 'import-row-error': row.error, 'import-row-warn': !row.error && row.warning }"
          >
            <td>{{ row.line }}</td>
            <td><code class="sim-code">{{ row.code }}</code></td>
            <td>{{ row.name || "—" }}</td>
            <td class="has-text-right">
              {{
                po.priceImportTargetField === "default_price"
                  ? row.current_default_price != null
                    ? po.money(row.current_default_price)
                    : "—"
                  : row.current_price != null
                    ? po.money(row.current_price)
                    : "—"
              }}
            </td>
            <td class="has-text-right sim-price-new">
              {{ row.new_price != null ? po.money(row.new_price) : row.cmim_oferte != null ? po.money(row.cmim_oferte) : "—" }}
            </td>
            <td>
              <span v-if="row.error" class="tag is-danger is-light">{{ row.error }}</span>
              <span v-else-if="row.warning" class="tag is-warning is-light">{{ row.warning }}</span>
              <span v-else class="tag is-success is-light">OK</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="result-card" v-if="po.priceImportApplyResult" style="margin-top: 10px">
      <div class="result-item"><strong>Targetuar:</strong> {{ po.priceImportApplyResult.targeted }}</div>
      <div class="result-item"><strong>Perditesuar:</strong> {{ po.priceImportApplyResult.updated }}</div>
      <div class="result-item"><strong>Financa 1 + Financa 2 OK:</strong> {{ po.priceImportApplyResult.synced_ok }}</div>
      <div class="result-item"><strong>Financa 1 / Financa 2 dështuar:</strong> {{ po.priceImportApplyResult.synced_failed }}</div>
      <div class="preview-table-wrap" v-if="po.priceImportApplyResult.failed_sync && po.priceImportApplyResult.failed_sync.length">
        <table class="table is-fullwidth is-narrow">
          <thead>
            <tr>
              <th>Kodi</th>
              <th>Financa</th>
              <th>Status</th>
              <th>Arsyeja</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(f, i) in po.priceImportApplyResult.failed_sync" :key="`sync-${f.product_id}-${f.erp || ''}-${i}`">
              <td>{{ f.code }}</td>
              <td>{{ po.erpLabel(f.erp) }}</td>
              <td>{{ f.status || "-" }}</td>
              <td>{{ f.reason || "-" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PriceImportPanel",
  inject: ["po"],
};
</script>
